
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useServiceWithAuth } from '@/hooks/useServiceWithAuth.ts';
import { GroupsService, type Group } from '@/services/features/GroupsService.ts';

export const useGroups = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { hasuraToken } = useAuth();

  // Get service instance with automatic auth context updates
  const groupsService = useServiceWithAuth(GroupsService);

  const fetchGroups = async () => {
    if (!hasuraToken) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await groupsService.fetchActiveGroups();

      if (!response.success) {
        throw new Error(response.errors?.[0] || 'Failed to fetch groups');
      }

      const groupsData = response.data?.pep_vw_group_details || [];
      // Map API GroupDetails to UI Group format
      const mappedGroups = groupsData.map((group: any) => ({
        ...group,
        es_id: [group.es_id], // Convert single number to array for UI compatibility
      }));
      setGroups(mappedGroups);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching groups:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [hasuraToken]);

  return { groups, isLoading, error, refetch: fetchGroups };
};
